/**
 * api-endpoints/axiosInstance.ts
 * Shared axios instance for SK Upsell.
 * - withCredentials: true → sends SK-Platform's httpOnly session cookie on every
 *   request (host-only on localhost, so it reaches :3010 and :3013 alike).
 * - SK Upsell owns NO auth UI. But the access-token cookie is short-lived, so a
 *   mid-session request can 401 even though the user is still validly logged in.
 *   A response interceptor transparently rotates the token ONCE via the
 *   platform's silent refresh (POST /v1/auth/refresh) and replays the original
 *   request, so screens never see a spurious 401. If the refresh itself fails
 *   (the 30-day session is truly gone), we hand off to the centralized platform
 *   auth — the only place login lives. Mirrors SK-Catalog's pattern exactly.
 *
 * Base URL is intentionally empty — services use the absolute URLs from
 * endpoints.ts (which read process.env).
 */
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { Platform_Refresh_Url } from './endpoints';
import { redirectToPlatformAuth } from '../navigation/platformAuthRedirect';

const axiosInstance = axios.create({
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
});

/* ── Auto-trim string inputs ───────────────────────────────────────────────
 * Trim every string in the request body once, centrally, so nothing persists
 * with stray whitespace. Format-significant fields are left untouched. */
const NO_TRIM_KEYS = new Set(['password', 'token', 'code', 'secret']);

function trimDeep(value: unknown, key?: string): unknown {
  if (typeof value === 'string') {
    return key && NO_TRIM_KEYS.has(key) ? value : value.trim();
  }
  if (Array.isArray(value)) return value.map((v) => trimDeep(v));
  if (
    value &&
    typeof value === 'object' &&
    ((value as object).constructor === Object || (value as object).constructor === undefined)
  ) {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(value as Record<string, unknown>)) {
      out[k] = trimDeep((value as Record<string, unknown>)[k], k);
    }
    return out;
  }
  return value;
}

axiosInstance.interceptors.request.use(
  (config) => {
    const d = config.data;
    const isPlain =
      d &&
      typeof d === 'object' &&
      !(typeof FormData !== 'undefined' && d instanceof FormData) &&
      !(typeof Blob !== 'undefined' && d instanceof Blob);
    if (isPlain) config.data = trimDeep(d);
    return config;
  },
  (error) => Promise.reject(error),
);

/* ── Silent refresh (single-flight) ─────────────────────────────────────────
 * A 401 from any data endpoint means the short-lived access cookie expired —
 * NOT that the user is logged out. Rotate it once via the platform refresh and
 * replay the request. A 401 from the platform auth endpoints themselves is
 * terminal (the refresh failed / session gone), so they never re-trigger. */
const NO_REFRESH = ['/auth/refresh', '/auth/logout', '/auth/login', '/auth/signup'];

let refreshInFlight: Promise<void> | null = null;

function refreshSession(): Promise<void> {
  // Single-flight: many parallel 401s share ONE refresh call.
  if (!refreshInFlight) {
    refreshInFlight = axiosInstance
      .post(Platform_Refresh_Url)
      .then(() => undefined)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;
    const status = error.response?.status;
    const url = original?.url ?? '';

    const refreshable =
      status === 401 &&
      !!original &&
      !original._retried &&
      !NO_REFRESH.some((p) => url.includes(p));

    if (refreshable && original) {
      try {
        await refreshSession();
        original._retried = true;
        return await axiosInstance(original);
      } catch {
        // Refresh failed → the 30-day session is gone. Hand off to the
        // centralized platform auth (guarded against double-redirects), then
        // surface the original 401 so callers stop.
        redirectToPlatformAuth();
        return Promise.reject(error);
      }
    }

    // Pass through — services catch this; errorHandler extracts the message.
    return Promise.reject(error);
  },
);

export default axiosInstance;
